// Store Zustand per autenticazione con persist
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, AuthTokens } from '@fieldcore/types'

// Estende il tipo User con campo name computato e twoFactorEnabled per compatibilità UI
type UserWithExtras = User & { name: string; twoFactorEnabled?: boolean }

interface AuthState {
  user: UserWithExtras | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  requiresTwoFactor: boolean
  pendingEmail: string | null
}

interface AuthActions {
  setAuth: (tokens: AuthTokens, user: User) => void
  setRequiresTwoFactor: (email: string) => void
  clearAuth: () => void
  updateUser: (user: Partial<UserWithExtras>) => void
  updateTokens: (tokens: Partial<AuthTokens>) => void
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      // Stato iniziale
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      requiresTwoFactor: false,
      pendingEmail: null,

      // Azioni
      setAuth: (tokens, user) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          user: { ...user, name: `${user.firstName} ${user.lastName}` },
          isAuthenticated: true,
          requiresTwoFactor: false,
          pendingEmail: null,
        }),

      setRequiresTwoFactor: (email) =>
        set({
          requiresTwoFactor: true,
          pendingEmail: email,
          isAuthenticated: false,
        }),

      clearAuth: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          requiresTwoFactor: false,
          pendingEmail: null,
        }),

      updateUser: (partial) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...(partial as Partial<UserWithExtras>) } : null,
        })),

      updateTokens: (partial) =>
        set((state) => ({
          accessToken: partial.accessToken ?? state.accessToken,
          refreshToken: partial.refreshToken ?? state.refreshToken,
        })),
    }),
    {
      name: 'fc-auth',
      // Persiste solo i token e l'utente, non i flag di UI
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
